import { Component, Entity, registerSystem, System } from "aframe";
import { Object3D } from "three";
import { AmmoBodyComponent, AmmoClosestRayResultCallback, AmmoGlobal, AmmoPhysicsSystem } from "../ammo";
import { AnchorComponent } from "../anchor";
import { AvatarSystem } from "../avatar";
import { ConnectionComponent } from "../connection";
import { InputActionDetail, InputActionEvent } from "../input/common";
import { MouseInputSystem } from "../input/mouse";
import { Lazy } from "../utils";
import { InteractiveComponent } from "./button-and-trigger";
import { AvatarInteractionTarget } from "./interaction-target";
declare var Ammo : AmmoGlobal;



export type InteractiveEntity = Entity< { [name:string] : Component } & {
	anchor? : AnchorComponent,
	trigger? : InteractiveComponent,
	button? : InteractiveComponent
} >;


export interface IInteractionTarget
{
	reset() : IInteractionTarget;
	update(detectedEntity : Entity, collidingEntity? : Entity) : IInteractionTarget;
	finish(avatarSystem : AvatarSystem, force?:boolean) : IInteractionTarget;
}


/*

TODO : rewrite to make abstraction of left/right hands. just use a generic array of interactors (selectorAll / SlotEntity[]).

*/

export interface AvatarInteractionSystem extends System, IInteractionTarget
{
	data : {
		leftHandSlot : string,
		rightHandSlot : string,
		collisionGroup : string[],
		collisionMask : string[],
		longActionTimeout : number
	},
	ray? : Lazy<Entity>;
	leftHand : AvatarInteractionTarget;
	rightHand : AvatarInteractionTarget;
}



const interactiveComponents : { [name:string] : any } =
{
	"slot" : 1,
	"anchor" : 1,
	"button" : 1,
	"trigger" : 1,
	"lever" : 1
};



// Slots in held items might be out of detection range. Traverse the objects to find detectable slots and simulate a detection :
const updateWithAllDetectableSlots = (el : Entity, obj : Object3D & { el? : Entity }, target : IInteractionTarget) =>
{
	if( obj.el === el )
	{
		if( el.components.slot )
		{
			for(let key in el.components)
			{
				const comp = el.components[key] as AmmoBodyComponent;
				if( (comp.name == "ammo-body") && comp.data?.enabled )
				{
					target.update(el);
				}
			}
		}
	}
	else if( obj.el )
	{
		el = obj.el;
	}

	for(let child of obj.children)
		updateWithAllDetectableSlots(el, child, target);
};



registerSystem("avatar-interaction", 
{
	schema : {
		leftHandSlot : { type:"string", default: "left-hand" },
		rightHandSlot : { type:"string", default: "right-hand" },
		collisionGroup : { type : "array", default : [] },
		collisionMask : { type : "array", default : [] },
		longActionTimeout : { type : "int", default: 500 }
	},


	init(this: AvatarInteractionSystem )
	{
		const sceneEl = this.el;
		const avatarSystem = sceneEl.systems.avatar as AvatarSystem;

		this.leftHand = new AvatarInteractionTarget("Left");
		this.rightHand = new AvatarInteractionTarget("Right");

		const resetHands = () => this.reset().finish(avatarSystem, true);

		sceneEl.addEventListener("enter-ui", () =>
		{
			resetHands();
			sceneEl.addEventListener("enter-mouse", resetHands);
			sceneEl.addEventListener("exit-mouse", resetHands);
		} );

		sceneEl.addEventListener("exit-ui", () =>
		{
			resetHands();
			sceneEl.removeEventListener("enter-mouse", resetHands);
			sceneEl.removeEventListener("exit-mouse", resetHands);
		} );

		const mouseSystem = sceneEl.systems.mouse as MouseInputSystem;

		const inputBeginListener = (event : InputActionEvent) =>
		{
			const detail = event.detail;
			if((detail.input && detail.input.name) != "mouse") return;
			if(!this.ray) return;

			const buttonAction = mouseSystem.data["0"], doubleClickAction = mouseSystem.data["Double0"];
			if(detail.action != buttonAction)  //Not the main button
				if(detail.action != doubleClickAction)  //Not the main button double-click
					return;
			
			const rayEl = this.ray.get();
			if(!rayEl) return;

			let action = "";
			for(let hand of [this.rightHand, this.leftHand] )
			{
				if(!avatarSystem.actions[ hand.mainAction ]) continue;
				if(rayEl !== hand.currentTarget)
					if(rayEl !== hand.currentSlot)
						if(rayEl !== hand.currentAnchor)
							continue;
				action = hand.mainAction;
				break;
			}
			if(!action) return;

			event.preventDefault();
			event.stopPropagation();

			if(detail.action != buttonAction)
				return; //Just prevent double click default

			const newDetail : InputActionDetail = {
				action,
				timestamp : Date.now(),
				input : detail.input,
				amount : detail.amount,
				feedback : detail.feedback,
				getRay : detail.getRay,
				instant : detail.instant
			};
			(event.target as Entity).emit("input-begin", newDetail);

			if(detail.instant) return; //No end

			const endListener = ((endEvent : InputActionEvent) =>
			{
				if(endEvent.target !== event.target) return;
				if(endEvent.detail !== detail) return;
				
				newDetail.amount = 0;
				(event.target as Entity).emit("input-end", newDetail);
				sceneEl.removeEventListener("input-end", endListener, { capture : true } );
			}) as EventListener;

			sceneEl.addEventListener("input-end", endListener, { capture : true } );

		};
		
		sceneEl.addEventListener("input-begin", inputBeginListener as EventListener, { capture : true } );
	},


	tick(this : AvatarInteractionSystem)
	{
		const scene = this.el;
		if(scene.is("vr-mode"))
		{

		}
		else if(scene.is("ui-mode"))
		{
			this.reset().finish( scene.systems["avatar"] as AvatarSystem );
		}
	},
	
	// Implement IInteractionTarget.reset for whole body (both hands)
	reset(this : AvatarInteractionSystem)
	{
		const uiMode = this.el.is("ui-mode");

		const physicsSystem = this.el.systems.physics as AmmoPhysicsSystem;
		const getMouseRay = () =>
		{
			const mouseSystem = this.el.systems.mouse as MouseInputSystem;
			if(!(mouseSystem.mousePosition || mouseSystem.pointer))
				return; //No persistent cursor on this device.
	
			const collisionFilterGroup = physicsSystem.getCollisionFilter(this.data.collisionGroup);
			const collisionFilterMask = physicsSystem.getCollisionFilter(this.data.collisionMask);

			const result : AmmoClosestRayResultCallback = new Ammo.ClosestRayResultCallback();
			const start = result.m_rayFromWorld, end = result.m_rayToWorld;
			mouseSystem.getRay(start, end, Ammo.btVector3.prototype.setValue);
			end.op_mul( 10.0 / end.length() ).op_add(start);
			result.m_collisionFilterGroup = collisionFilterGroup;
			result.m_collisionFilterMask = collisionFilterMask;

			physicsSystem.driver.physicsWorld.rayTest(start, end, result);

			if(result.hasHit() && result.m_collisionObject)
			{
				const rayBody = physicsSystem.components[ Ammo.getPointer(result.m_collisionObject) ];
				Ammo.destroy(result);
				const rayEl = rayBody && rayBody.el;
				if(!rayEl) return;

				for(let key in interactiveComponents)
					if(key in rayEl.components)
						return rayEl;

				//rayEl has no interactive component, maybe it's connected to one ?
				const connection = rayEl.components.connection as ConnectionComponent;
				if(connection && connection.data && connection.data.components)
					for(let key in interactiveComponents)
						if(key in connection.data.components)
							return connection.data;
				
				return null;
			}

			Ammo.destroy(result);
		};

		const ray = new Lazy<Entity>(getMouseRay);
		this.ray = ray;
		this.leftHand.reset(uiMode, ray);
		this.rightHand.reset(uiMode, ray);

		if(uiMode)
		{
			const rayEl = ray.get();
			rayEl && this.update(rayEl, undefined);
		}

		return this;
	},

	// Implement IInteractionTarget.update for whole body (both hands)
	update(this : AvatarInteractionSystem, detectedEntity: Entity, collidingEntity?: Entity)
	{
		//Ignore detections of items in hands
		const lSlot = this.leftHand.slot && this.leftHand.slot.components.slot.data;
		if(lSlot && (lSlot.anchor === detectedEntity)) return this;
		const rSlot = this.rightHand.slot && this.rightHand.slot.components.slot.data;
		if(rSlot && (rSlot.anchor === detectedEntity)) return this;
		
		this.leftHand.update(detectedEntity, collidingEntity);
		this.rightHand.update(detectedEntity, collidingEntity);

		return this;
	},

	// Implement IInteractionTarget.finish for whole body (both hands)
	finish(this : AvatarInteractionSystem, avatarSystem: AvatarSystem, force?: boolean | undefined) 
	{
		const lSlot = this.leftHand.slot && this.leftHand.slot.components.slot.data;
		const rSlot = this.rightHand.slot && this.rightHand.slot.components.slot.data;
		const lItem = lSlot && lSlot.item, rItem = rSlot && rSlot.item;

		//If both hands hold items, allow them to detect the other hand's item if there's no other target.
		//The only valid interaction with an held item is to put it in a slot, so let's detect slots :
		if( lItem && rItem ) 
		{
			if( !this.leftHand.currentTarget )
				updateWithAllDetectableSlots(rItem, rItem.object3D, this.leftHand);
			if( !this.rightHand.currentTarget )
				updateWithAllDetectableSlots(lItem, lItem.object3D, this.rightHand);
		}

		this.leftHand.finish(avatarSystem, force);
		this.rightHand.finish(avatarSystem, force);

		let firstHand = this.rightHand, secondHand = this.leftHand;
		if(rItem && !lItem) //Left hand is free : prefer it
		{
			firstHand = this.leftHand;
			secondHand = this.rightHand;
		}

		const mainAction = avatarSystem.actions[ firstHand.mainAction ] || avatarSystem.actions[ secondHand.mainAction ];
		this.el.classList.toggle("main-action", Boolean(mainAction));
		this.el.style.cursor = (mainAction && this.el.is('ui-mode') && mainAction.cursor) || "default";
		avatarSystem.setAction("Action", mainAction );
		avatarSystem.setAction("Use", avatarSystem.actions[ firstHand.useAction ] || avatarSystem.actions[ secondHand.useAction ]  );
		avatarSystem.setAction("Grab", avatarSystem.actions[ firstHand.grabAction ] || avatarSystem.actions[ secondHand.grabAction ]  );
		avatarSystem.setAction("Fire", avatarSystem.actions[ firstHand.fireAction ] || avatarSystem.actions[ secondHand.fireAction ]  );
		return this;
	}
});


